import { useTranslation } from 'next-i18next/pages';

import {
  SOLUTION_RESOURCE_FORMAT_LABEL_KEYS,
  SOLUTION_RESOURCE_PRODUCT_NAMES,
  type SolutionResource,
} from '@/data/shared/solutionResources';

import useSolutionResourceDownload from '@/hooks/apps/useSolutionResourceDownload';

import { IconArrowUpRightFromSquareRound } from '@/components/apps/ui/icon';

import theme from '@/styles/theme';

const CARD_CLASS =
  'group flex h-full w-full flex-col justify-between gap-[24px] rounded-[12px] bg-apps-coreGrayWhite px-[20px] py-[20px] shadow-[0px_0px_20px_0px_rgba(0,0,0,0.04)] transition-[box-shadow] duration-200 ease-[ease] hover:shadow-[0px_0px_24px_0px_rgba(0,0,0,0.08)] lg:rounded-[20px] lg:gap-[clamp(24px,2.571vw,36px)] lg:px-[clamp(20px,2.571vw,36px)] lg:py-[clamp(20px,2.571vw,36px)]';

const FORMAT_BADGE_CLASS =
  'inline-flex w-fit items-center rounded-[6px] bg-apps-coreGray07 px-[10px] py-[4px] text-[13px] font-semibold leading-[140%] tracking-[-0.26px] text-apps-brandGray200 lg:text-[14px] lg:tracking-[-0.28px]';

const TITLE_CLASS =
  'text-[16px] font-bold leading-[140%] tracking-[-0.32px] text-apps-brandGray50 transition-[color] duration-300 ease-[ease] group-hover:text-apps-corePrimary01 lg:text-[clamp(16px,1.833vw,22px)] lg:leading-[160%] lg:tracking-[-0.44px]';

const PRODUCT_CLASS =
  'text-[14px] font-semibold leading-[140%] tracking-[-0.28px] text-apps-brandPrimary500 lg:text-[16px] lg:tracking-[-0.32px]';

const ACTION_CLASS =
  'flex items-center justify-between gap-[8px] border-t border-apps-coreGray07 pt-[16px] text-[14px] font-semibold leading-[140%] tracking-[-0.28px] text-apps-brandGray200 lg:text-[16px] lg:tracking-[-0.32px]';

const ACTION_ICON_CLASS =
  'inline-flex min-h-[32px] min-w-[32px] items-center justify-center rounded-[8px] transition-[background-color] duration-200 ease-[ease] group-hover:bg-[rgba(22,22,23,0.06)]';

type Props = {
  resource: SolutionResource;
};

/**
 * ### ResourceDownloadCard
 *
 * One resource on the resource page, printing its format, title, and product
 * so a card read on its own still says which document it is. The anchor is a
 * plain element rather than the locale-aware link component, because the
 * download path already carries the language in its own segment.
 *
 * @param props.resource Resource the card offers
 */
export default function ResourceDownloadCard({ resource }: Props) {
  const { t } = useTranslation('common');
  const { href, onClick, title } = useSolutionResourceDownload(resource);

  return (
    <a
      className={CARD_CLASS}
      href={href}
      onClick={onClick}
      rel="noopener"
      target="_blank"
    >
      <div className="flex flex-col gap-[8px]">
        <span className={FORMAT_BADGE_CLASS}>
          {t(SOLUTION_RESOURCE_FORMAT_LABEL_KEYS[resource.format])}
        </span>
        <span className={TITLE_CLASS}>{title}</span>
        <span className={PRODUCT_CLASS}>
          {SOLUTION_RESOURCE_PRODUCT_NAMES[resource.product]}
        </span>
      </div>
      <span className={ACTION_CLASS}>
        {t('다운로드')}
        <span aria-hidden="true" className={ACTION_ICON_CLASS}>
          <IconArrowUpRightFromSquareRound
            color={theme`colors.apps.coreGrayBlack`}
            size={24}
          />
        </span>
      </span>
    </a>
  );
}
